import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { useRef } from 'react';
import RouterSideNav from 'ui-addon-react-router-dom/RouterSideNav';
import RouterSideNavItem from 'ui-addon-react-router-dom/RouterSideNavItem';
import { STANDALONE_CORE_SEATS } from '../../../constants/ReleaseInsights';
import { NavItemEnum, NavSectionEnum } from '../../../constants/routing/NavItems';
import { useHasReleaseInsight } from '../../../hooks/useBulkPurchaseOptions';
import { useShouldHideDataOrSuitePage } from '../../../hooks/useShouldHideDataOrSuitePage';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { SegmentedNavItems } from '../../../constants/routing/NavItems';
import { prefetchPage } from '../../../routes/loadablePages';
import { useAddOnsPage } from '../../../v2/hooks/add-ons/useAddOnsPage';
import { useCommerceHub } from '../../../v2/hooks/gating/useCommerceHub';
import { useHubSpotCredits } from '../../../v2/hooks/gating/useHubSpotCredits';
import { useMarketingPlus } from '../../../v2/hooks/gating/useMarketingPlus';
import { useIsFreePortal } from '../../../v2/hooks/inventory/useProductInventory';
import { useIsInAppAuth } from '../../../v2/hooks/user/useUserInfo';
import { usePreserveQueryParams } from '../../../v2/hooks/utilities/usePreserveQueryParams';
import CurrencyPicker from '../../common/CurrencyPicker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useSideNavItems = () => {
  const isInAppAuth = useIsInAppAuth();
  const isPublic = !isInAppAuth;
  const {
    showMarketingPlus
  } = useMarketingPlus();
  const shouldHideDataOrSuitePage = useShouldHideDataOrSuitePage();
  const {
    hasReleaseInsight: isSmartCrm,
    loading: isLoadingSmartCrm
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const {
    showHubSpotCredits
  } = useHubSpotCredits();
  const {
    showAddonsPage
  } = useAddOnsPage();
  const {
    showCommerceHub
  } = useCommerceHub();
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const segmentedNavItems = SegmentedNavItems;
  const shouldShowNavSection = section => {
    switch (section) {
      case NavSectionEnum.ENHANCEMENTS:
        return showAddonsPage || showHubSpotCredits;
      default:
        return true;
    }
  };
  const shouldShowNavItem = item => {
    switch (item) {
      case NavItemEnum.MARKETING_PLUS:
        return showMarketingPlus;
      case NavItemEnum.ADD_ONS:
        return showAddonsPage;
      case NavItemEnum.HUBSPOT_CREDITS:
        return showHubSpotCredits;
      case NavItemEnum.FREE:
        return isPublic || isFreePortal || isInventoryLoading;
      case NavItemEnum.BUNDLE:
        return isPublic;
      case NavItemEnum.SMART_CRM:
        return !isLoadingSmartCrm && isSmartCrm;
      case NavItemEnum.COMMERCE:
        return showCommerceHub;
      case NavItemEnum.DATA:
      case NavItemEnum.SUITE:
        return !shouldHideDataOrSuitePage;

      // return true for all other nav items by default
      default:
        return true;
    }
  };
  const visibleSections = Object.keys(segmentedNavItems).filter(shouldShowNavSection);
  const visibleItemsBySection = Object.fromEntries(visibleSections.map(section => [section, segmentedNavItems[section].filter(shouldShowNavItem)]));
  return {
    visibleSections,
    visibleItemsBySection
  };
};
const SideNav = ({
  selectedNavItem
}) => {
  var _ref$current;
  const ref = useRef(null);
  const sideNavDomRect = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.getBoundingClientRect();
  // calculates offset of the side nav's bottom from the top of the viewport, accounts for navbar
  const sideNavTop = (sideNavDomRect === null || sideNavDomRect === void 0 ? void 0 : sideNavDomRect.top) || 0;
  const {
    height
  } = useWindowSize();
  const isInAppAuth = useIsInAppAuth();
  const createToWithQueryParams = usePreserveQueryParams();
  const {
    visibleSections,
    visibleItemsBySection
  } = useSideNavItems();
  return /*#__PURE__*/_jsx(View, {
    position: isInAppAuth ? 'fixed' : 'sticky',
    insetBlockStart: "24px",
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      id: "scroll-container",
      scrollDirection: "vertical"
      // -48 to account for the insetBlockStart=24px of the two Views
      ,
      maxHeight: `${height - (sideNavTop - 48)}px`,
      overflowY: "auto",
      children: /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "900",
        marginBlockEnd: "1300",
        blockSize: "100%",
        paddingInlineStart: "200",
        children: /*#__PURE__*/_jsx(View, {
          insetBlockStart: "24px",
          children: /*#__PURE__*/_jsxs("div", {
            ref: ref,
            children: [visibleSections.map((navSection, index) => /*#__PURE__*/_jsx(RouterSideNav, {
              divider: index !== 0,
              headingText: I18n.text(`pricingPage.sidenav.sections.${navSection}`),
              children: visibleItemsBySection[navSection].map(navItem => /*#__PURE__*/_jsx(RouterSideNavItem, {
                "data-test-id": `side-nav-${navItem}`,
                "data-is-active": selectedNavItem === navItem,
                "data-selenium-class": "side-nav",
                active: selectedNavItem === navItem,
                title: /*#__PURE__*/_jsx(Text, {
                  textWrap: "nowrap",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `pricingPage.pages.products.tabs.${navItem}.tabTitle`
                  })
                }),
                to: createToWithQueryParams(`/${navItem}`),
                padding: "400",
                marginInlineStart: "300",
                onMouseEnter: () => prefetchPage(navItem)
              }, navItem))
            }, navSection)), /*#__PURE__*/_jsx(CurrencyPicker, {})]
          })
        })
      })
    })
  });
};
export default SideNav;